/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { defineStore } from 'pinia';
import type {
  RootState,
  TValue,
  Field,
  Payload,
  UserGroups,
  Timeline,
  GeneralLinks,
  HistoryResponse,
  MessageResponse,
  MetaData,
  UserResponse,
  MetaDataResponse,
  AdditionalTValues,
  PreTValue,
  Module,
  ExpertSearch,
  ExpSearchItem,
  ExpertSearchGroup,
} from '@/store/root/types';
import { isObject } from '@/helper/typeGuards';
import { deepGet, toArray, deepClone, orderBy } from '@/helper/lodashHelper';
import axios from '@/axios';
import { $i18n } from '@/provider/plugin';
import { useContextLinksStore } from '@/store/contextLinks';
import localStorageHelper from '@/helper/localStorageHelper';
import indexedDBHelper from '@/helper/indexedDBHelper';
import { mergeCurrentLocale } from '@/plugins/i18n';
import { $settings } from '@/provider/settings';
import { authService } from '@/services/auth';
import { teammembersConfig } from '@/components/cockpits/teams/config/config';
import { useOutlookStore } from '@/store/outlook';
import { useAuthStore } from '@/store/auth';
import { useDashboardStore } from '@/store/dashboard';
import { useObjectStore } from '@/store/object';

const additionalTValues: AdditionalTValues = {
  visibility: [
    { alias: 'WX_DASHB_PUBLIC', value: 'public', icon: 'internet' },
    { alias: 'WX_DASHB_PRIVATE', value: 'private', icon: 'person' },
    { alias: 'WX_DASHB_TEAM', value: 'team', icon: 'team' },
  ],
  segmentationResult: [
    { alias: 'SEGMENTATION_CUST_CP', value: 'custCp' },
    { alias: 'SEGMENTATION_CUST_ONLY', value: 'custOnly' },
    { alias: 'SEGMENTATION_CP_ONLY', value: 'cpOnly' },
  ],
};

const getRelatedTValues = function (
  tValuesState: TValue[],
  field: Field,
  payload: Payload,
) {
  const { tValCond, recordBind } = field;

  return tValuesState.filter((singleTValue) => {
    const relsCheck: boolean[] = [];
    const searchString = /%(.*?)%/gi;
    let relcodeNumber = 0;

    if (tValCond !== undefined) {
      toArray(tValCond).forEach((relcode) => {
        const match = relcode.match(searchString);
        relcodeNumber += 1;
        let value = '';

        if (!!match && payload) {
          const dynamicValue = match[0].replaceAll('%', '');
          const hasRecordBind = dynamicValue.split('.').length > 1;
          const dynamicValueFull = hasRecordBind
            ? dynamicValue
            : `${recordBind}.${dynamicValue}`;
          const dynPayload = payload[recordBind]
            ? payload
            : { [recordBind]: payload };

          const getValue = deepGet(dynPayload, dynamicValueFull);

          if (getValue !== undefined) {
            value = getValue;
          }
        } else {
          value = relcode;
        }

        const relcodes: (string | undefined)[] =
          value && Array.isArray(value) ? value : [value];
        const matchingTValue = singleTValue[`relcode${relcodeNumber}`];
        relsCheck.push(relcodes.includes(matchingTValue));
      });
    }

    return relsCheck.find((check) => !check) !== false;
  });
};

const getTValue = function (
  state: RootState,
  field: Field,
  value: string,
  payload: Payload,
) {
  const { tValue, tValCond } = field;
  const fallBack: object = { text: value };
  if (!tValue || !value) return fallBack;

  const tValuesState = state.tValues[tValue]?.filter(
    (item: TValue) => item.value === value,
  );

  if (!Array.isArray(tValuesState) || tValuesState.length === 0)
    return fallBack;

  if (tValCond) {
    const relatedTValues = getRelatedTValues(tValuesState, field, payload);
    return relatedTValues?.length > 0 ? relatedTValues[0] : {};
  }

  return tValCond
    ? getRelatedTValues(tValuesState, field, payload)
    : tValuesState[0];
};

const getTValueFields = function (
  state: RootState,
  field: Field,
  payload: Payload,
): TValue[] {
  const { tValCond, tValue } = field;
  if (tValue === undefined) return [];

  const tValuesState = state.tValues[tValue.trim()] ?? [];
  return tValCond
    ? getRelatedTValues(tValuesState, field, payload)
    : tValuesState;
};

const getCustomSearch = (
  customSearch: ExpertSearch,
  filter: keyof ExpSearchItem,
): ExpertSearch => {
  if (!customSearch || !filter) {
    return customSearch || {};
  }

  return Object.fromEntries(
    Object.entries(orderBy(customSearch, 'position')).filter(([, item]) =>
      Boolean(item[filter]),
    ),
  );
};

const getExpertSearchGroup = (state: RootState) => {
  if (!state.expertSearchGroup || state.expertSearchGroup.length === 0)
    return [];

  const modifiedOverviewBar: Record<
    string,
    ExpSearchItem & { type: string; index: number }
  > =
    state.expertSearch && isObject(state.expertSearch)
      ? Object.fromEntries(
          Object.entries(state.expertSearch).map(
            (
              [type, overview],
              index,
            ): [string, ExpSearchItem & { type: string; index: number }] => [
              type,
              { ...overview, type, index },
            ],
          ),
        )
      : {};

  return orderBy(
    state.expertSearchGroup.map((item) => ({
      ...item,
      overviewBar: orderBy(
        item.expSearches?.map((overviewItem) => ({
          ...modifiedOverviewBar[overviewItem.type],
          ...overviewItem,
        })),
        'position',
      ),
    })),
    'position',
  );
};

export const useRootStore = defineStore('root', {
  state: (): RootState => ({
    initialized: false,
    parentObject: {},
    tValues: {},
    aliases: {},
    userGroups: {},
    generalHistory: [],
    generalMessages: [],
    generalLinks: [],
    device: 'desktop',
    width: 0,
    timeline: [],
    expertSearch: {},
    expertSearchGroup: [],
    customSearch: {},
  }),
  getters: {
    getIsInitialized: (state) => state.initialized,
    getParentObject: (state) => state.parentObject,
    getTValue:
      (state) => (field: Field) => (value: string) => (payload: Payload) =>
        getTValue(state, field, value, payload),
    getTValueFields: (state) => (field: Field) => (payload: Payload) =>
      getTValueFields(state, field, payload),
    getTValues: (state) => state.tValues,
    getUserGroups: (state) => state.userGroups,
    getGeneralLinks: (state) => state.generalLinks,
    getGeneralHistory: (state) => state.generalHistory,
    getGeneralMessages: (state) => state.generalMessages,
    getTimeline: (state) => state.timeline || [],
    getExpertSearch: (state) => orderBy(state.expertSearch, 'position') || {},
    getExpertSearchGroup: (state) => getExpertSearchGroup(state),
    getCustomSearch: (state) => (filter: keyof ExpSearchItem) =>
      getCustomSearch(state.customSearch, filter),
  },
  actions: {
    setParentObject(data: Record<string, string>) {
      this.parentObject = data;
    },

    async init() {
      const metadata = await this.getMetadata();
      if (metadata !== null) {
        this.setLocalesFromMetadata(metadata);
        await this.initModules(metadata);
      }

      this.initialized = true;
    },

    async getMetadata(retryCounter = 0): Promise<MetaData | null> {
      if (
        !(await authService.isAuthenticated()) ||
        $settings.useBasicAuth === true
          ? !useAuthStore().basicAuth
          : false
      ) {
        return null;
      }

      // If developer has manually overridden metadata, skip middleware fetch
      if (window.localStorage.getItem('devMetaOverride') === 'true') {
        const meta = (await indexedDBHelper.getData('meta')) as MetaData;
        this.setLocalesFromMetadata(meta);
        this.initModules(meta);
        return meta;
      }

      try {
        const { userGroup } = await this.getUserInformations();
        const timestamp = localStorageHelper.getTimestamp();

        const response = await axios<MetaDataResponse>({
          url: `${$settings.middlewareURI}/metadata/${userGroup}?timestamp=${timestamp}`,
          validateStatus: (status) => status >= 200 && status < 400,
        });

        if (response.data) {
          await indexedDBHelper.setData('meta', response.data);
          localStorageHelper.setTimestamp();
          return response.data;
        }

        if (response.status === 304) {
          console.info(
            'No metadata changes since last request, retrieving from DB.',
          );
        } else {
          console.info('No metadata from middleware, retrieving from DB.');
        }

        const localMetadata = (await indexedDBHelper.getData(
          'meta',
        )) as MetaData;
        return localMetadata;
      } catch {
        if (retryCounter < 3) {
          console.info('Could not retrieve metadata.');
          localStorageHelper.clearTimestamp();
          return this.getMetadata(retryCounter + 1);
        }
        return null;
      }
    },
    updateWindowWidth(width: number) {
      if (Math.abs(width - this.width) > 10) this.width = width;
      this.setCurrentDevice(width);
    },

    getCurrentDevice(width: number) {
      if (width < 667) return 'mobile';
      if (width <= 1024) return 'tablet';
      return 'desktop';
    },

    setCurrentDevice(width: number) {
      this.device = this.getCurrentDevice(width);
    },

    setUserGroups(data: UserGroups) {
      this.userGroups = data;
    },

    setTValues(preTValue: PreTValue) {
      const { tValues, tValColors, additionals } = preTValue;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const returnObject: Record<string, TValue[]> = deepClone(tValues);

      Object.entries(tValColors).forEach(([index, items]) => {
        returnObject[index] = returnObject[index]!.map((item: TValue) => ({
          ...items.find(({ value }) => value === item.value),
          ...item,
        }));
      });

      toArray(additionals).forEach((tValue) => {
        Object.entries(tValue).forEach(
          ([key, value]: [string, TValue[]]): void => {
            returnObject[key] = value.map((item: TValue): TValue => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const text: string | undefined = item.alias
                ? $i18n.global.t(item.alias)
                : item.text;
              return {
                ...item,
                text,
              };
            });
          },
        );
      });

      this.tValues = returnObject;
    },

    setAliases(data: Record<string, string>) {
      this.aliases = data;
    },

    setTimeline(data: Timeline[]) {
      this.timeline = data;
    },

    setExpertSearch(expertSearch: ExpertSearch) {
      // TODO: BE refactor structure (contextLinks, modules: {activity...})
      delete expertSearch.contextLinks;
      this.expertSearch = expertSearch;
    },

    setExpertSearchGroup(expertSearchGroup: ExpertSearchGroup) {
      this.expertSearchGroup = expertSearchGroup;
    },

    setCustomSearch(modules: Record<string, Module>): void {
      this.customSearch = Object.entries(modules).reduce(
        (accumulator, [type, module]) =>
          module?.expSearch?.alias &&
          module.expSearch?.position &&
          'readOnly' in module.expSearch
            ? { ...accumulator, [type]: module.expSearch }
            : accumulator,
        {},
      );
    },

    setGeneralLinks(data: GeneralLinks[]) {
      this.generalLinks = data;
    },

    async fetchGeneralHistory() {
      const response = await axios<HistoryResponse>({
        method: 'get',
        url: `${$settings.middlewareSAPProxyURI}/lastused/query`,
      });

      if (response?.data?.DATA)
        this.generalHistory = response.data.DATA.QUERYRESULT;
    },

    async fetchGeneralMessages() {
      const response = await axios<MessageResponse>({
        method: 'get',
        url: `${$settings.middlewareSAPProxyURI}/lastmsg`,
      });

      if (response?.data?.SUCCESS)
        this.generalMessages = response.data.DATA.QUERYRESULT;
    },

    // TODO: separate module Erweiterungsmethode für die Daten aus der Config
    extendMetadata(modules: Record<string, Module>): Record<string, Module> {
      const teammembers = teammembersConfig;
      return { ...modules, teammembers };
    },

    async initModules(meta: MetaData) {
      if (!meta) return null;
      const {
        tValues,
        tValColors,
        generalLinks,
        aliases,
        timeline,
        expSearch,
        expSearchGroup,
      } = meta;

      let { modules } = meta;

      modules = this.extendMetadata(modules);

      const additionals = [
        { WX_VISIBILITY: additionalTValues.visibility },
        { SEGMENTATION_RESULT: additionalTValues.segmentationResult },
      ] as Record<string, TValue[]>[];

      const preTValue: PreTValue = {
        tValues,
        tValColors,
        additionals,
      };

      this.setTValues(preTValue);
      this.setGeneralLinks(generalLinks);
      this.setAliases(aliases);

      await useDashboardStore().fetchDashboards();

      this.setTimeline(timeline);

      this.setExpertSearch(expSearch);
      this.setExpertSearchGroup(expSearchGroup);
      this.setCustomSearch(modules);

      Object.entries(modules).forEach(([type, metaConfig]) => {
        try {
          useObjectStore(type).init({ meta: metaConfig ?? {} });

          useContextLinksStore().setContextLinks({
            type,
            contextLinks: metaConfig?.contextLinks ?? {},
          });
        } catch (error) {
          console.warn(error);
        }
      });

      if (window.Office) {
        // eslint-disable-next-line no-unused-vars
        const mailItemChanged = async () => {
          // Update UI & mailid based on the new current item
          await useOutlookStore().updateEmailStore(
            window.Office.context.mailbox.item,
          );
        };

        window.Office.context.mailbox.addHandlerAsync(
          window.Office.EventType.ItemChanged,
          mailItemChanged,
        );

        await useOutlookStore().updateEmailStore(
          window.Office.context.mailbox.item,
        );
      }

      return meta;
    },

    async getUserInformations() {
      const response = await axios.get<UserResponse>(
        `${$settings.middlewareSAPProxyURI}/userinfo`,
      );
      const userInformation = response.data;
      if (userInformation)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.setUserGroups({ ...userInformation, teamAdmin: true });
      return userInformation;
    },

    setLocalesFromMetadata(meta: MetaData): void {
      if (!meta?.aliases) return;
      mergeCurrentLocale([meta.aliases]);
    },
  },
});
